<template>
    <condo-field v-if="!disableCondoField" :label="title" autocomplete="chrome-off" label-position="on-border">
        <n-select
            :clear-filter-after-select="clearAfterSelect"
            :clearable="clearable"
            :disabled="disabled"
            :loading="loading"
            :max-tag-count="2"
            :multiple="multiple"
            :on-update:value="contactSelected"
            :options="options"
            :render-label="renderContactLabel"
            :size="size"
            :value="selectedContacts"
            filterable
            optionKey="key"
            remote
            v-bind="$attrs"
            @search="getFilteredContacts"
        >
            <template #action>
                <slot name="action" />
                <div v-if="multiple" class="is-flex is-justify-content-space-between">
                    <div>
                        <n-button @click="selectAll">All</n-button>
                    </div>
                    <div>
                        <n-button @click="unselectAll">None</n-button>
                    </div>
                </div>
            </template>
            <template #empty>
                <span>Start typing to search contacts</span>
            </template>
        </n-select>
    </condo-field>
    <n-select
        v-else
        :clear-filter-after-select="clearAfterSelect"
        :clearable="clearable"
        :disabled="disabled"
        :loading="loading"
        :max-tag-count="2"
        :multiple="multiple"
        :on-update:value="contactSelected"
        :options="options"
        :render-label="renderContactLabel"
        :size="size"
        :value="selectedContacts"
        filterable
        optionKey="key"
        remote
        v-bind="$attrs"
        @search="getFilteredContacts"
    >
        <template #action>
            <slot name="action" />
            <div class="is-flex is-justify-content-space-between">
                <div>
                    <n-button @click="selectAll">All</n-button>
                </div>
                <div>
                    <n-button @click="unselectAll">None</n-button>
                </div>
            </div>
        </template>
        <template #empty>
            <span>Start typing to search contacts</span>
        </template>
    </n-select>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { getContacts } from '@/api/contacts.api';
import { renderContactLabel } from '@/components/finance/render.helpers';

import { CompanyFunction, getContactDisplayName } from '@condo/domain';
import { NSelect, NButton } from 'naive-ui';

interface Props {
    contactType?: string;
    disabled?: boolean;
    contactTypes?: string[];
    companyFunction?: string;
    estateId?: number;
    relationName?: string[];
    contactDisplayProp?: string;
    title?: string;
    clearAfterSelect?: boolean;
    clearable?: boolean;
    defaultContactIds?: number[];
    disableItemsWithNoEmail?: boolean;
    multiple?: boolean;
    disableCondoField?: boolean;
    labelFn?: (contact: any) => string;
    size?: 'tiny' | 'small' | 'medium' | 'large';
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    contactDisplayProp: 'name',
    title: 'Contact',
    clearAfterSelect: true,
    clearable: false,
    disableItemsWithNoEmail: false,
    multiple: false,
    disableCondoField: false,
});

const emit = defineEmits<{
    contactSelected: [contacts: any];
}>();

const selectedContacts = defineModel<any>({ default: [] });
const contactMap = ref<Map<number, any>>(new Map());
const searchResults = ref<any[]>([]);
const loading = ref(false);

const options = computed(() => {
    const selectedIds = Array.isArray(selectedContacts.value) ? selectedContacts.value : [selectedContacts.value].filter(Boolean);

    const selectedContacts_ = selectedIds.map(id => contactMap.value.get(id)).filter(Boolean);

    const merged = [...selectedContacts_];
    for (const c of searchResults.value) {
        if (!selectedIds.includes(c.contactId)) {
            merged.push(c);
        }
    }

    const opts = merged.map(toContactOption);

    if (props.disableItemsWithNoEmail) {
        opts.sort((a, b) => Number(a.disabled) - Number(b.disabled));
    }

    return opts;
});

const getDefaultContacts = async (defaultContactIds: number[]) => {
    if (!defaultContactIds?.length) {
        return;
    }

    loading.value = true;

    const { contacts: fetchedContacts } = await getContacts({
        query: { contactId: defaultContactIds },
        expand: {
            select: ['contactId', 'email', 'firstname', 'lastname', 'fullname', 'salutation', 'contactType'],
        },
    });
    for (const c of fetchedContacts) {
        contactMap.value.set(c.contactId, c);
    }
    searchResults.value = fetchedContacts;

    const selected = fetchedContacts.filter(c => !props.disableItemsWithNoEmail || (props.disableItemsWithNoEmail && !!c?.email));
    const selectedIds = selected.map(c => c.contactId);

    contactSelected(props.multiple ? selectedIds : (selectedIds[0] ?? null));

    loading.value = false;
};

const getFilteredContacts = async (query: string) => {
    if (!query) {
        return;
    }

    loading.value = true;

    const searchParams: Record<string, any> = { query, page: 1, size: 20 };
    if (props.contactType) {
        searchParams.contactType = [props.contactType];
    } else if (props.contactTypes) {
        searchParams.contactType = props.contactTypes;
    }

    if (props.companyFunction) {
        searchParams.companyFunction = CompanyFunction.PropertyManager;
    }

    if (props.estateId) {
        searchParams.estateId = props.estateId;
        // relationName cannot be used without estateId
        if (props.relationName) {
            searchParams.relationName = Array.isArray(props.relationName)
                ? props.relationName
                : typeof props.relationName === 'string'
                  ? [props.relationName]
                  : undefined;
        }
    }

    const { contacts: fetchedContacts } = await getContacts({
        query: searchParams,
        expand: {
            select: ['contactId', 'email', 'firstname', 'lastname', 'fullname', 'salutation', 'contactType'],
        },
    });
    for (const c of fetchedContacts) {
        contactMap.value.set(c.contactId, c);
    }
    searchResults.value = fetchedContacts;

    loading.value = false;
};

const contactSelected = (value: any) => {
    selectedContacts.value = value;
    const ids = Array.isArray(value) ? value : [value].filter(Boolean);
    const selectedObjects = ids.map(id => contactMap.value.get(id)).filter(Boolean);
    emit('contactSelected', selectedObjects);
};

const toContactOption = (contact: any) => {
    const displayName = getContactDisplayName(contact);
    const defaultLabel = props.disableItemsWithNoEmail ? `${displayName} ${contact?.email ? ' ✉️' : ' ❌'}` : displayName;
    const label = props.labelFn ? props.labelFn(contact) : defaultLabel;

    return {
        key: contact?.contactId,
        label,
        value: contact?.contactId,
        disabled: props.disableItemsWithNoEmail && !contact?.email,
        contact,
    };
};

const selectAll = () => {
    const allIds = options.value.filter(opt => !opt.disabled).map(opt => opt.value);
    contactSelected(allIds);
};

const unselectAll = () => {
    contactSelected([]);
};

watch(
    () => props.defaultContactIds,
    async newValue => {
        if (!newValue) {
            selectedContacts.value = [];
            return;
        }
        await getDefaultContacts(newValue);
    },
    { immediate: true },
);
</script>
